import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Coins, Gift, Copy, Check, Sparkles, ArrowDownCircle, ArrowUpCircle, Clock, Trophy } from 'lucide-react';

const POINTS_PER_POUND = 1;
const POINTS_PER_DISCOUNT_POUND = 100;

const TYPE_LABEL = {
  earn_purchase: 'Shop purchase',
  earn_booking: 'Booking',
  redeem: 'Redeemed',
  expire: 'Expired',
  admin_adjust: 'Adjustment',
};

export default function LoyaltyPointsOverview({ email }) {
  const queryClient = useQueryClient();
  const [redeemPts, setRedeemPts] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  const { data: ledger = [], isLoading } = useQuery({
    queryKey: ['loyalty-ledger', email],
    queryFn: () => base44.entities.LoyaltyLedger.filter({ customer_email: email }, '-created_date', 100),
    enabled: !!email,
  });

  const { data: summary } = useQuery({
    queryKey: ['loyalty-summary', email],
    queryFn: async () => {
      const res = await base44.functions.invoke('getLoyaltySummary', {});
      return res.data || res;
    },
    enabled: !!email,
    refetchInterval: 30000,
  });

  const lastActivity = summary?.lastActivity;
  const expiryDate = summary?.expiryDate;
  const tallyBalance = summary?.tallyBalance || 0;
  const tallyProgress = summary?.tallyProgress || 0;

  const balance = ledger.reduce((s, r) => s + Number(r.points || 0), 0);
  const earned = ledger.filter((r) => Number(r.points) > 0).reduce((s, r) => s + Number(r.points), 0);
  const redeemed = ledger.filter((r) => Number(r.points) < 0).reduce((s, r) => s + Math.abs(Number(r.points)), 0);
  const redeemValue = Math.floor((Number(redeemPts) || 0) / POINTS_PER_DISCOUNT_POUND);

  const handleRedeem = async () => {
    setErr('');
    setResult(null);
    const pts = Math.floor(Number(redeemPts));
    if (!pts || pts < POINTS_PER_DISCOUNT_POUND) {
      setErr(`Minimum ${POINTS_PER_DISCOUNT_POUND} points per redemption.`);
      return;
    }
    setRedeeming(true);
    try {
      const res = await base44.functions.invoke('redeemLoyaltyPoints', { points: pts });
      const data = res.data || res;
      if (data.success === false) {
        setErr(data.error || 'Could not redeem points.');
      } else {
        setResult(data);
        setRedeemPts('');
        queryClient.invalidateQueries({ queryKey: ['loyalty-ledger', email] });
      }
    } catch (e) {
      setErr(e.message || 'Could not redeem points.');
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = () => {
    if (!result?.code) return;
    navigator.clipboard.writeText(result.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Balance hero */}
      <div className="bg-gradient-to-br from-primary/15 via-[#111115] to-[#111115] border border-primary/30 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Coins className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">LOYALTY POINTS</p>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-white font-black text-4xl leading-none">{balance.toLocaleString()}</p>
            <p className="text-white/40 text-xs mt-1.5">points available to spend</p>
          </div>
          <div className="text-right">
            <p className="text-primary font-black text-xl leading-none">£{(balance / POINTS_PER_DISCOUNT_POUND).toFixed(2)}</p>
            <p className="text-white/30 text-[10px] mt-1">redeemable value</p>
          </div>
        </div>
        {lastActivity && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-primary/15">
            <Clock className="w-3 h-3 text-primary/60 shrink-0" />
            <p className="text-white/40 text-[11px]">
              Last activity {new Date(lastActivity).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {expiryDate && <> · points expire {new Date(expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</>}
            </p>
          </div>
        )}
      </div>

      {/* Free-session tally */}
      <div className="bg-[#111115] border border-white/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">FREE SESSION TALLY</p>
        </div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-white font-black text-2xl leading-none">{tallyProgress}<span className="text-white/30 text-base">/20</span></p>
            <p className="text-white/40 text-xs mt-1">sessions towards your next free experience</p>
          </div>
          {tallyBalance > 0 && (
            <span className="text-primary font-bold text-xs tracking-widest uppercase border border-primary/30 bg-primary/10 px-2.5 py-1">
              {tallyBalance} free available
            </span>
          )}
        </div>
        <div className="w-full h-2 bg-white/5 overflow-hidden">
          <div className="h-full bg-primary telemetry-gauge" style={{ width: `${Math.min(100, (tallyProgress / 20) * 100)}%` }} />
        </div>
      </div>

      {/* Earn rate info */}
      <div className="bg-[#111115] border border-white/5 p-5 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p className="text-white/50 text-sm leading-relaxed">
          Earn <span className="text-white font-bold">{POINTS_PER_POUND} point{POINTS_PER_POUND !== 1 ? 's' : ''} per £1</span> on shop purchases. Book a session to add to your free-session tally (1 free per 20).
          Redeem points at <span className="text-white font-bold">{POINTS_PER_DISCOUNT_POUND} points = £1 off</span> your next order.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#111115] border border-white/5 p-4">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">EARNED</p>
          <p className="text-white font-black text-lg mt-1">{earned.toLocaleString()}</p>
        </div>
        <div className="bg-[#111115] border border-white/5 p-4">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">REDEEMED</p>
          <p className="text-white font-black text-lg mt-1">{redeemed.toLocaleString()}</p>
        </div>
        <div className="bg-primary/5 border border-primary/20 p-4">
          <p className="text-primary text-[10px] font-bold tracking-widest">BALANCE</p>
          <p className="text-primary font-black text-lg mt-1">{balance.toLocaleString()}</p>
        </div>
      </div>

      {/* Redeem */}
      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-4 h-4 text-primary" />
          <h2 className="text-white font-bold text-sm tracking-widest uppercase">Redeem Points</h2>
        </div>
        {result ? (
          <div className="text-center py-4">
            <Check className="w-10 h-10 text-primary mx-auto mb-3" />
            <p className="text-white font-bold mb-1">Your discount code is ready</p>
            <p className="text-white/40 text-xs mb-3">Worth £{result.value} · {result.pointsSpent} points spent</p>
            <div className="flex items-center justify-center gap-2 mb-4">
              <code className="bg-[#0d0d0f] border border-primary/30 text-primary font-mono text-base px-4 py-2 tracking-widest">{result.code}</code>
              <button onClick={copyCode} className="border border-white/10 hover:border-white/30 text-white/60 hover:text-white p-2 transition-colors" title="Copy code">
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-white/30 text-xs mb-4">Enter this code at checkout to apply your discount. Single use only.</p>
            <button onClick={() => setResult(null)} className="text-white/50 hover:text-white text-xs font-bold tracking-widest uppercase">
              Redeem More
            </button>
          </div>
        ) : (
          <>
            <p className="text-white/40 text-xs mb-3">
              You have {balance.toLocaleString()} points. Minimum {POINTS_PER_DISCOUNT_POUND} points per redemption.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="number"
                value={redeemPts}
                onChange={(e) => setRedeemPts(e.target.value)}
                placeholder="Points to redeem"
                min={POINTS_PER_DISCOUNT_POUND}
                className="flex-1 bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/50 transition-colors"
              />
              <button
                onClick={handleRedeem}
                disabled={redeeming || balance < POINTS_PER_DISCOUNT_POUND}
                className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {redeeming ? 'REDEEMING...' : `REDEEM${redeemValue > 0 ? ` £${redeemValue}` : ''}`}
              </button>
            </div>
            {err && <p className="text-red-400 text-xs mt-3">{err}</p>}
          </>
        )}
      </div>

      {/* History */}
      <div className="bg-[#111115] border border-white/5 p-6">
        <p className="text-white/40 text-xs font-bold tracking-widest mb-4">POINT HISTORY</p>
        {ledger.length === 0 ? (
          <p className="text-white/30 text-sm text-center py-6">No activity yet. Earn points with your next booking or purchase.</p>
        ) : (
          <div className="space-y-2">
            {ledger.map((r) => {
              const pts = Number(r.points || 0);
              const positive = pts >= 0;
              return (
                <div key={r.id} className="flex items-center gap-3 border-b border-white/5 pb-2 last:border-0 last:pb-0">
                  {positive ? <ArrowUpCircle className="w-4 h-4 text-green-400 shrink-0" /> : <ArrowDownCircle className="w-4 h-4 text-amber-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-white/70 text-sm truncate">{r.description || TYPE_LABEL[r.type] || r.type}</p>
                    <p className="text-white/30 text-[10px]">{new Date(r.created_date || r.updated_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <p className={`font-mono text-sm font-bold ${positive ? 'text-green-400' : 'text-amber-400'}`}>
                    {positive ? '+' : ''}{pts.toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}